import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useEffect, useState } from 'react'
import { activityTemplateService } from '../../../../services/activityTemplateService'
import { useAuthContext } from '../../../../hooks/useAuthContext'
import { adminTeamsService } from '../../../../services/admin/teams'
import adminDocumentTemplateService from '../../../../services/admin/document'
import { toast } from 'react-toastify'
import ConfirmationBox from '../../../../components/Dashboard/ConfirmationBox'
import { adminNotificationService } from '../../../../services/admin/notification'
import { santizeHtml } from '../../../../utility/utilityFunctions'
import { formatUserDisplayName } from '../../../../utility/userName'

const CustomNotificationTemplates = () => {
  const { user } = useAuthContext()

  const initialModalData = {
    id: 0,
    template_name: '',
    trigger_activity_template_id: null,
    user_ids: [],
    team_ids: [],
    body: '',
    subject: '',
    static_attachments: [],
    document_template_ids: [],
    trigger_route_template_ids: [],
  }

  const [loading, setLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [modalData, setModalData] = useState({})
  const [allTemplates, setAllTemplates] = useState([])
  const [allActivities, setAllActivities] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [allTeams, setAllTeams] = useState([])
  const [allRoutes, setAllRoutes] = useState([])
  const [allDocumentTemplates, setAllDocumentTemplates] = useState([])
  const [openDeleteFile, setOpenDeleteFile] = useState(false)
  const [deleteFileDetails, setDeleteFieldDetails] = useState({ name: '', index: null })
  const [openDeleteTemplate, setOpenDeleteTemplate] = useState(false)
  const [deleteTemplateDetails, setDeleteTemplateDetails] = useState({ name: '', id: null })

  useEffect(() => {
    if (user) {
      ;(async () => {
        setLoading(true)
        const allNotificationTemplates =
          await adminNotificationService.getCustomNotificationTemplates(user)
        const allActivitiesData = await activityTemplateService.getAllActivitiesOfCompany(
          user,
          user.company_id,
        )
        const teams = await adminTeamsService.getTeams(user)
        const users = await adminTeamsService.getUsers(user)

        setAllTemplates(allNotificationTemplates)
        setAllActivities(allActivitiesData)
        setAllTeams(teams)
        setAllUsers(users)
        setLoading(false)
      })()
    }
  }, [user])

  useEffect(() => {
    if (openModal && modalData.trigger_activity_template_id) {
      ;(async () => {
        const activityToRoutes = (
          await activityTemplateService.getAllToRoutes(user, modalData.trigger_activity_template_id)
        ).data
        const activityDocumentTemplates =
          await adminDocumentTemplateService.getDocumentTemplatesForActivity(
            user,
            modalData.trigger_activity_template_id,
          )

        setAllRoutes(activityToRoutes)
        setAllDocumentTemplates(activityDocumentTemplates)
        if (modalData.trigger_route_template_ids.length == 0) {
          setModalData({
            ...modalData,
            trigger_route_template_ids: activityToRoutes?.map((it) => it.route_template_id) || [],
          })
        }
      })()
    } else {
      setAllRoutes([])
      setAllDocumentTemplates([])
    }
  }, [user, openModal, modalData.trigger_activity_template_id])

  const handleOpenModal = (data = initialModalData) => {
    setModalData(data)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setModalData(initialModalData)
  }

  const handleDownloadStaticDocument = (base64, fileName) => {
    const link = document.createElement('a')
    link.href = base64
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleChange = (type, value) => {
    switch (type) {
      case 'static_attachments':
        if (value.size > 5 * 1024 * 1024) {
          toast.error('File size should not exceed 5 MB.')
          break
        }

        const reader = new FileReader()

        reader.onload = () => {
          const base64 = reader.result
          const prevUploadedFiles = modalData?.static_attachments || []
          setModalData({
            ...modalData,
            [type]: [...prevUploadedFiles, { base64: base64, file_name: value?.name || '' }],
          })
        }
        reader.onerror = () => {
          toast.error('An error occurred while reading the file.')
        }
        reader.readAsDataURL(value)
        break
      case 'trigger_activity_template_id':
        setModalData({ ...modalData, [type]: value, trigger_route_template_ids: [] })
        break
      default:
        setModalData({ ...modalData, [type]: value })
        break
    }
  }

  const handleCreateOrUpdateTemplate = async () => {
    try {
      if (
        modalData?.template_name.length == 0 ||
        modalData?.trigger_activity_template_id == null ||
        modalData?.body?.length == 0
      ) {
        return toast.error('Please fill the required fields')
      }
      setButtonLoading(true)
      const sanitizedHtml = santizeHtml(modalData.body)
      const updatedModalData = { ...modalData, body: sanitizedHtml }
      if (updatedModalData.id) {
        await adminNotificationService.updateCustomNotificationTemplate(
          user,
          updatedModalData.id,
          updatedModalData,
        )

        const updatedAllTemplates = allTemplates.map((template) => {
          if (template.id == updatedModalData.id) {
            return updatedModalData
          }
          return template
        })
        toast.success('Template updated successfully!')
        setAllTemplates(updatedAllTemplates)
      } else {
        const result = await adminNotificationService.saveCustomNotificationTemplate(
          user,
          updatedModalData,
        )
        toast.success('Template created successfully!')
        setAllTemplates([...allTemplates, { ...updatedModalData, id: result }])
      }
      handleCloseModal()
      setButtonLoading(false)
    } catch (error) {
      toast.error('Something went wrong')
      handleCloseModal()
      setButtonLoading(false)
    }
  }

  const handleDeleteTemplate = async () => {
    await adminNotificationService.deleteCustomNotificationTemplate(user, deleteTemplateDetails.id)

    const updateAllTemplates = allTemplates.filter(
      (template) => template.id != deleteTemplateDetails.id,
    )

    setAllTemplates(updateAllTemplates)

    toast.error('Template deleted successfully!')

    setOpenDeleteTemplate(false)
    setDeleteTemplateDetails({ name: '', id: null })
  }

  const handleDeleteStaticFile = () => {
    let updatedStaticAttachments = modalData.static_attachments.filter((value, i) => {
      return value?.file_name !== deleteFileDetails.name && i != deleteFileDetails.index
    })
    setModalData({ ...modalData, static_attachments: updatedStaticAttachments })
    setOpenDeleteFile(false)
    setDeleteFieldDetails({ name: '', index: null })
  }

  if (loading) {
    return (
      <div
        style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress color="secondary" size={40} />
      </div>
    )
  }

  return (
    <Box mt={2}>
      <Box style={{ display: 'flex', gap: 16, marginBottom: '2rem' }}>
        <Typography variant="h5" fontWeight="bold">
          Custom Notification Templates
        </Typography>
      </Box>
      <Box>
        <Box mb="1rem" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button color="secondary" variant="contained" onClick={() => handleOpenModal()}>
            Create New Template
          </Button>
        </Box>

        <Box maxHeight={'65vh'} overflow={'scroll'}>
          {allTemplates.length > 0 ? (
            allTemplates.map((template) => (
              <Card elevation={1} style={{ padding: 16, margin: 8 }} key={template.id}>
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Tooltip title={template.template_name}>
                      <Typography variant="h6" style={{ margin: 8 }}>
                        #{template.id}. {template.template_name}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box style={{ display: 'flex', columnGap: 8 }}>
                    <Tooltip title="Edit Template">
                      <IconButton
                        onClick={() => handleOpenModal(template)}
                        style={{ backgroundColor: '#f2eeed', height: 32, width: 32 }}
                        size="small"
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Template">
                      <IconButton
                        onClick={() => {
                          setOpenDeleteTemplate(true)
                          setDeleteTemplateDetails({
                            name: template.template_name,
                            id: template.id,
                          })
                        }}
                        style={{ backgroundColor: '#f2eeed', height: 32, width: 32 }}
                        size="small"
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <>No Templates</>
          )}
        </Box>
      </Box>

      {/*  Add/Edit Template Modal  */}
      {openModal && (
        <Dialog open={openModal} fullWidth maxWidth="lg" onClose={handleCloseModal}>
          <DialogTitle>{modalData.id ? 'Edit Template' : 'Create Template'}</DialogTitle>
          <DialogContent>
            <Box p={'10px'} sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <TextField
                size="small"
                label="Template Name (required)"
                value={modalData.template_name}
                onChange={(e) => handleChange('template_name', e.target.value)}
                color="secondary"
              />
              <Autocomplete
                size="small"
                options={allActivities}
                getOptionLabel={(option) =>
                  `${option.process_template_name} / ${option.activity_template_name}`
                }
                value={
                  allActivities.find(
                    (activity) =>
                      activity.activity_template_id === modalData?.trigger_activity_template_id,
                  ) || null
                }
                isOptionEqualToValue={(option, value) =>
                  option.activity_template_id == value?.activity_template_id
                }
                onChange={(_, newValue) =>
                  handleChange('trigger_activity_template_id', newValue?.activity_template_id)
                }
                fullWidth
                renderInput={(params) => (
                  <TextField color="secondary" {...params} label="Select Activity (required)" />
                )}
              />
              <Box
                sx={{
                  position: 'relative',
                  border: '1px solid rgba(0, 0, 0, 0.3)',
                  padding: '16px',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    position: 'absolute',
                    top: '-12px',
                    left: '16px',
                    background: '#fff',
                    padding: '0 8px',
                    color: 'rgba(0, 0, 0, 0.4)',
                    fontSize: '13px',
                  }}
                >
                  To
                </Typography>

                <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                  <Autocomplete
                    size="small"
                    options={allUsers}
                    getOptionLabel={(option) => formatUserDisplayName(option)}
                    value={allUsers.filter((user) => modalData?.user_ids?.includes(user.id)) || []}
                    isOptionEqualToValue={(option, value) => option.id == value?.id}
                    onChange={(_, newValue) =>
                      handleChange(
                        'user_ids',
                        newValue.map((it) => it.id),
                      )
                    }
                    fullWidth
                    multiple
                    renderInput={(params) => (
                      <TextField color="secondary" {...params} label="Select Users" />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    options={allTeams}
                    getOptionLabel={(option) => `${option.name} (${option.team_type})`}
                    value={allTeams.filter((team) => modalData?.team_ids?.includes(team.id)) || []}
                    isOptionEqualToValue={(option, value) => option.id == value?.id}
                    onChange={(_, newValue) =>
                      handleChange(
                        'team_ids',
                        newValue.map((it) => it.id),
                      )
                    }
                    fullWidth
                    multiple
                    renderInput={(params) => (
                      <TextField color="secondary" {...params} label="Select Teams" />
                    )}
                  />
                </Box>
              </Box>
              <TextField
                size="small"
                label="Subject"
                value={modalData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                color="secondary"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Body (required)"
                  multiline
                  fullWidth
                  minRows={7}
                  maxRows={15}
                  value={modalData.body}
                  onChange={(e) => handleChange('body', e.target.value)}
                  color="secondary"
                  placeholder={`
                    To add process field data, Use inbound server action keys like [[first_name]] not [[f_1234]].
                    
                    <html>
                    <body>
                      <p>Hi [[first_name]],</p>
                      <p>I'm mailing you regarding [[purpose]].</p>
                      <p>Something</p>
                    </body>
                    </html>
                    `}
                />
                <Tooltip title="Template Body Preview">
                  <IconButton
                    size="small"
                    onClick={() => setShowPreview(true)}
                    style={{ backgroundColor: '#f2eeed', height: 32, width: 32 }}
                  >
                    <VisibilityIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  border: '1px solid rgba(0, 0, 0, 0.3)',
                  padding: '16px',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    position: 'absolute',
                    top: '-12px',
                    left: '16px',
                    background: '#fff',
                    padding: '0 8px',
                    color: 'rgba(0, 0, 0, 0.4)',
                    fontSize: '13px',
                  }}
                >
                  Attachment(s)
                </Typography>

                <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                  <div
                    style={{
                      width: '100%',
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      component="label"
                      fullWidth
                      sx={{ marginBottom: 1 }}
                    >
                      Add Static Document
                      <input
                        hidden
                        type="file"
                        onChange={(e) => handleChange('static_attachments', e.target.files[0])}
                      />
                    </Button>
                    <div style={{ marginBottom: 2 }}>
                      {modalData?.static_attachments?.length > 0
                        ? modalData.static_attachments.map((doc, index) => (
                            <div key={index}>
                              <Typography variant="caption">{doc.file_name}</Typography>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDownloadStaticDocument(doc.base64, doc.file_name)
                                  }
                                >
                                  <DownloadIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setOpenDeleteFile(true)
                                    setDeleteFieldDetails({ name: doc.file_name, index })
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            </div>
                          ))
                        : null}
                    </div>
                  </div>
                  <Autocomplete
                    size="small"
                    options={allDocumentTemplates || []}
                    getOptionLabel={(option) => option?.document_template_name || ''}
                    value={
                      allDocumentTemplates
                        ? allDocumentTemplates.filter((doc) =>
                            modalData?.document_template_ids?.includes(doc.id),
                          )
                        : []
                    }
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onChange={(_, newValue) =>
                      handleChange(
                        'document_template_ids',
                        newValue?.map((it) => it.id),
                      )
                    }
                    fullWidth
                    multiple
                    renderInput={(params) => (
                      <TextField color="secondary" {...params} label="Dynamic Documents" />
                    )}
                  />
                </Box>
              </Box>
              <Autocomplete
                size="small"
                options={allRoutes || []}
                getOptionLabel={(option) => option?.route_template_name || ''}
                value={
                  allRoutes
                    ? allRoutes.filter((route) =>
                        modalData?.trigger_route_template_ids?.includes(route.route_template_id),
                      )
                    : []
                }
                isOptionEqualToValue={(option, value) =>
                  option?.route_template_id === value?.route_template_id
                }
                onChange={(_, newValue) =>
                  handleChange(
                    'trigger_route_template_ids',
                    newValue?.map((it) => it.route_template_id),
                  )
                }
                fullWidth
                multiple
                renderInput={(params) => (
                  <TextField color="secondary" {...params} label="Trigger Routes" />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="info" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={buttonLoading}
              onClick={handleCreateOrUpdateTemplate}
            >
              {modalData.id ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/*  Preview Template Body Modal  */}
      <Dialog open={showPreview} fullWidth maxWidth="lg" onClose={() => setShowPreview(false)}>
        <DialogTitle>Template Body Preview</DialogTitle>
        <DialogContent>
          <div
            dangerouslySetInnerHTML={{
              __html:
                modalData.body?.trim()?.length > 0
                  ? santizeHtml(modalData.body)
                  : `<p>No Preview available.</p>`,
            }}
          ></div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Static file Confirmation */}
      <ConfirmationBox
        isDialogOpen={openDeleteFile}
        setIsDialogOpen={setOpenDeleteFile}
        title={`Are you sure you want to delete "${deleteFileDetails?.name}" ?`}
        onYes={handleDeleteStaticFile}
      />

      {/* Delete Template Confirmation */}
      <ConfirmationBox
        isDialogOpen={openDeleteTemplate}
        setIsDialogOpen={setOpenDeleteTemplate}
        title={`Are you sure you want to delete "${deleteTemplateDetails?.name}" ?`}
        onYes={handleDeleteTemplate}
      />
    </Box>
  )
}

export default CustomNotificationTemplates
